/**
 * Low-level row-major grid helpers for map-gen.
 * Grids use every tile in `widthTiles` × `heightTiles` (0-indexed through width − 1 / height − 1).
 */

import type { ICoord2D, IRect, ITileGridCoord } from '@nova/types';

import { clampInt } from '../math-utils';

export interface ClampRectOriginInMapBoundsArgs {
  mapWidth: number;
  mapHeight: number;
  rectWidth: number;
  rectHeight: number;
  origin: ICoord2D;
  /**
   * Extra margin from map edges (tiles), same idea as POI `spawnBufferTiles`.
   * Default `0`. Not the presentation border ring (`borderOutsetTiles`).
   */
  edgeMarginTiles?: number;
}

export function tileIndexAt(col: number, row: number, width: number): number {
  return (row * width) + col;
}

/** Inverse of {@link tileIndexAt} for a row-major grid. */
export function tileIndexToColRow(tileIndex: number, widthTiles: number): ITileGridCoord {
  return {
    col: tileIndex % widthTiles,
    row: Math.floor(tileIndex / widthTiles)
  };
}

export function forEachTileIndexInRect(
  rect: IRect,
  widthTiles: number,
  heightTiles: number,
  visit: (tileIndex: number) => void
): void {
  const x0 = Math.max(0, Math.floor(rect.x));
  const y0 = Math.max(0, Math.floor(rect.y));
  const x1 = Math.min(widthTiles, x0 + Math.max(0, Math.floor(rect.width)));
  const y1 = Math.min(heightTiles, y0 + Math.max(0, Math.floor(rect.height)));
  for (let row = y0; row < y1; row++) {
    for (let col = x0; col < x1; col++) {
      visit(tileIndexAt(col, row, widthTiles));
    }
  }
}

/** Collects tile indices visited by {@link forEachTileIndexInRect}. */
export function getTileIndicesInRect(rect: IRect, widthTiles: number, heightTiles: number): number[] {
  const tiles: number[] = [];
  forEachTileIndexInRect(rect, widthTiles, heightTiles, (tileIndex) => {
    tiles.push(tileIndex);
  });
  return tiles;
}

/**
 * Tile indices whose centers lie within `radiusTiles` (Euclidean) of `center` in world space.
 * Uses continuous tile coordinates so even-sized stamps stay symmetric around a world center.
 * When `out` is provided it is cleared and filled; otherwise a new array is allocated.
 */
export function getTileIndicesInWorldRadius(
  center: ICoord2D,
  radiusTiles: number,
  tilePx: number,
  widthTiles: number,
  heightTiles: number,
  out?: number[]
): number[] {
  const tiles = out ?? [];
  tiles.length = 0;

  if (tilePx <= 0 || widthTiles <= 0 || heightTiles <= 0 || radiusTiles < 0) {
    return tiles;
  }

  const centerCol = center.x / tilePx;
  const centerRow = center.y / tilePx;
  const radiusSq = radiusTiles * radiusTiles;
  const radiusPx = radiusTiles * tilePx;
  const minCol = Math.max(0, Math.floor((center.x - radiusPx) / tilePx));
  const maxCol = Math.min(widthTiles - 1, Math.floor((center.x + radiusPx) / tilePx));
  const minRow = Math.max(0, Math.floor((center.y - radiusPx) / tilePx));
  const maxRow = Math.min(heightTiles - 1, Math.floor((center.y + radiusPx) / tilePx));

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      const dCol = (col + 0.5) - centerCol;
      const dRow = (row + 0.5) - centerRow;
      if (((dCol * dCol) + (dRow * dRow)) <= radiusSq) {
        tiles.push(tileIndexAt(col, row, widthTiles));
      }
    }
  }
  return tiles;
}

export interface ITileGridStep {
  dc: number;
  dr: number;
}

export const TILE_GRID_NEIGHBORS_4: readonly ITileGridStep[] = [
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 }
];

export const TILE_GRID_NEIGHBORS_8: readonly ITileGridStep[] = [
  ...TILE_GRID_NEIGHBORS_4,
  { dc: 1, dr: 1 },
  { dc: -1, dr: 1 },
  { dc: 1, dr: -1 },
  { dc: -1, dr: -1 }
];

/**
 * World position at the geometric center of a tile.
 * `originX`/`originY` are the world-space top-left of tile (0, 0).
 */
export function tileCenterWorldPx(col: number, row: number, tilePx: number, originX: number, originY: number): ICoord2D {
  return {
    x: originX + (col * tilePx) + (tilePx / 2),
    y: originY + (row * tilePx) + (tilePx / 2)
  };
}

/**
 * World position at the bottom-center of a tile (common anchor for tall props and characters).
 * `originX`/`originY` are the world-space top-left of tile (0, 0).
 */
export function tileBottomCenterWorldPx(col: number, row: number, tilePx: number, originX: number, originY: number): ICoord2D {
  return {
    x: originX + (col * tilePx) + (tilePx / 2),
    y: originY + ((row + 1) * tilePx)
  };
}

export function tileValueAt(grid: Uint8Array, width: number, height: number, col: number, row: number): number {
  if (col < 0 || row < 0 || col >= width || row >= height) {
    return 0;
  }
  return grid[tileIndexAt(col, row, width)];
}

export function setTileValue(grid: Uint8Array, width: number, height: number, col: number, row: number, value: number): void {
  if (col < 0 || row < 0 || col >= width || row >= height) {
    return;
  }
  grid[tileIndexAt(col, row, width)] = value;
}

export function countTilesWithValue(grid: Uint8Array, value: number): number {
  let count = 0;
  for (let i = 0; i < grid.length; i++) {
    if (grid[i] === value) {
      count++;
    }
  }
  return count;
}

/**
 * True when every cell of the axis-aligned `rect` is in bounds, currently 0,
 * and no `filledValue` tile lies within `gapTiles` of the rect (Chebyshev).
 */
export function rectFitsWithGap(grid: Uint8Array, width: number, height: number, rect: IRect, gapTiles: number, filledValue: number): boolean {
  const { x: col, y: row, width: wTiles, height: hTiles } = rect;
  if (col < 0 || row < 0 || col + wTiles > width || row + hTiles > height) {
    return false;
  }

  for (let r = row; r < row + hTiles; r++) {
    for (let c = col; c < col + wTiles; c++) {
      if (tileValueAt(grid, width, height, c, r) !== 0) {
        return false;
      }
    }
  }

  if (gapTiles <= 0) {
    return true;
  }

  const minC = col - gapTiles;
  const maxC = col + wTiles - 1 + gapTiles;
  const minR = row - gapTiles;
  const maxR = row + hTiles - 1 + gapTiles;

  for (let r = minR; r <= maxR; r++) {
    for (let c = minC; c <= maxC; c++) {
      if (c >= col && c < col + wTiles && r >= row && r < row + hTiles) {
        continue;
      }

      if (tileValueAt(grid, width, height, c, r) === filledValue) {
        return false;
      }
    }
  }

  return true;
}

/** Writes `value` into every in-bounds cell of `rect`. Returns how many cells changed. */
export function writeRectValue(grid: Uint8Array, width: number, height: number, rect: IRect, value: number): number {
  const { x: col, y: row, width: wTiles, height: hTiles } = rect;
  let changed = 0;
  for (let r = row; r < row + hTiles; r++) {
    for (let c = col; c < col + wTiles; c++) {
      if (c < 0 || r < 0 || c >= width || r >= height) {
        continue;
      }
      const index = tileIndexAt(c, r, width);
      if (grid[index] === value) {
        continue;
      }
      grid[index] = value;
      changed++;
    }
  }
  return changed;
}

/** Counts cells inside `rect` that currently hold `value`. */
export function countRectTilesWithValue(grid: Uint8Array, width: number, height: number, rect: IRect, value: number): number {
  const { x: col, y: row, width: wTiles, height: hTiles } = rect;
  let count = 0;
  for (let r = row; r < row + hTiles; r++) {
    for (let c = col; c < col + wTiles; c++) {
      if (c < 0 || r < 0 || c >= width || r >= height) {
        continue;
      }

      if (grid[tileIndexAt(c, r, width)] === value) {
        count++;
      }
    }
  }
  return count;
}

/** Marks each cell of `rect` with `fillValue` until `budget` is exhausted. Returns newly changed tiles. */
export function fillRectWithValue(grid: Uint8Array, width: number, height: number, rect: IRect, budget: number, fillValue: number): number {
  const { x: col, y: row, width: wTiles, height: hTiles } = rect;
  let added = 0;
  let filled = countTilesWithValue(grid, fillValue);

  for (let r = row; r < row + hTiles; r++) {
    for (let c = col; c < col + wTiles; c++) {
      if (filled >= budget) {
        return added;
      }

      if (tileValueAt(grid, width, height, c, r) !== 0) {
        continue;
      }
      setTileValue(grid, width, height, c, r, fillValue);
      filled++;
      added++;
    }
  }

  return added;
}

/** Clamps origin so the full rectangle stays inside the map. Returns null when the rect cannot fit. */
export function clampRectOriginInMapBounds(args: ClampRectOriginInMapBoundsArgs): ICoord2D | null {
  const edgeMargin = Math.max(0, Math.floor(args.edgeMarginTiles ?? 0));
  const rectWidth = Math.max(1, Math.floor(args.rectWidth));
  const rectHeight = Math.max(1, Math.floor(args.rectHeight));
  const maxX = args.mapWidth - edgeMargin - rectWidth;
  const maxY = args.mapHeight - edgeMargin - rectHeight;
  if (maxX < edgeMargin || maxY < edgeMargin) {
    return null;
  }

  const { origin } = args;
  return {
    x: clampInt(origin.x, edgeMargin, maxX),
    y: clampInt(origin.y, edgeMargin, maxY)
  };
}
